import React, { ReactNode } from 'react'
import { Box, styled, SvgIconProps } from '@mui/material'
import { IMessage } from '@weblite/ts-action/message'
import { TextParser } from '@weblite/web-textparser'
import { AudioMessageIcon } from '$common/icons/audio-message'
import { FileMessageIcon } from '$common/icons/file-message'
import { WappMessageIcon } from '$common/icons/wapp-message'
import { VoiceMessageIcon } from '$common/icons/voice-message'
import { VideoMessageIcon } from '$common/icons/video-message'
import { hyperLinkViewer } from '$common/hyperLink-viewer'
import { EMOJI_SPRITE_SHEET } from '$helper/constants/emoji'
import { ContactIcon } from '$common/icons'
import { FROM_ME_REPLY_BG, FROM_OTHER_REPLY_BG } from '$setup/style/constants'

const Container = styled('div')`
  /* @noflip */
  border-radius: 4px 3px 3px 4px;
  /* @noflip */
  padding: 5px 9px 5px 6px;
`

export const MessagePreviewRoot = styled(Container)<{ fromMe: boolean }>(
  ({ fromMe }) => ({
    display: 'flex',
    width: '100%',
    height: 48,
    alignItems: 'center',
    overflow: 'hidden',
    backgroundColor: fromMe ? FROM_ME_REPLY_BG : FROM_OTHER_REPLY_BG,
    position: 'relative',
    boxSizing: 'border-box',
  }),
)

const MessagePreviewBox = styled('div')`
  /* @noflip */
  right: 0;
`

export const MessagePreviewVerticalSideBox = styled(MessagePreviewBox)<{
  fromMe: boolean
}>(({ theme, fromMe }) => ({
  width: 3,
  height: '100%',
  backgroundColor: fromMe
    ? theme.palette.secondary100
    : theme.palette.secondary500,
  position: 'absolute',
  top: 0,
}))

export const MessagePreviewInfoContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex-grow: 1;
  gap: 2px;
  height: 100%;
  /* @noflip */
  margin-right: 6px;
  overflow: hidden;
`

export const MessagePreviewInfoRow = styled('div')`
  display: flex;
  align-items: center;
  overflow: hidden;
`

export const MessagePreviewMessageTypeIconMap: Record<
  IMessage['type'],
  React.ComponentType<SvgIconProps> | null
> = {
  IMAGE: null,
  TEXT: null,
  EVENT: null,
  CALL: null,
  AUDIO: AudioMessageIcon,
  FILE: FileMessageIcon,
  WAPP: WappMessageIcon,
  VOICE: VoiceMessageIcon,
  VIDEO: VideoMessageIcon,
  CONTACT: ContactIcon,
}

interface DescriptorContainerProps {
  hasMarginToIcon: boolean
}

export const MessagePreviewDescriptorContainer = styled('div')<
  DescriptorContainerProps
>`
  overflow: hidden;
  margin-right: ${props => (props.hasMarginToIcon ? 4 : 0)}px;
`

const TextParserContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.paragraph3,
  lineHeight: '16px',
  textAlign: 'initial',
  '--emoji-size': '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const parsers = [
  { name: 'emoji', args: { spritesheeturl: EMOJI_SPRITE_SHEET } },
  'code',
  { name: 'hyperLink', fn: hyperLinkViewer },
]
export function MessagePreviewMessageDescriptorTypography({
  color,
  fromMe,
  children: body,
}: {
  fromMe: boolean
  color?: string | null
  children: ReactNode
}) {
  return (
    <TextParserContainer
      dir="auto"
      sx={{ color: color ? color : fromMe ? 'neutral000' : 'neutral500' }}
    >
      <TextParser text={body} parsers={parsers} />
    </TextParserContainer>
  )
}

export * from './skeleton'
export * from './message-thumbnail'
